import { useState, useEffect, useContext, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Shield, LogOut, FolderOpen, Clock, CheckCircle, AlertCircle, 
  ChevronRight, Search, Filter, RefreshCw, BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthContext } from "../App";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  offen: { label: "Offen", color: "#A0E8AF", bgColor: "#A0E8AF20", icon: FolderOpen },
  in_bearbeitung: { label: "In Bearbeitung", color: "#F6E05E", bgColor: "#F6E05E20", icon: Clock },
  geschlossen: { label: "Geschlossen", color: "#A0AEC0", bgColor: "#A0AEC020", icon: CheckCircle }
};

const CATEGORY_CONFIG = {
  mobbing: { label: "Mobbing", color: "#FFC4D6" },
  cybermobbing: { label: "Cybermobbing", color: "#BDE0FE" },
  identitaetsdiebstahl: { label: "Identitätsdiebstahl", color: "#A0E8AF" },
  andere: { label: "Andere", color: "#F6E05E" }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, logout } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("alle");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Failed to fetch data", e);
      if (e.response?.status === 401) {
        toast.error("Sitzung abgelaufen. Bitte erneut anmelden.");
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = 
      report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "alle" || report.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unbekannt";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-100 p-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img 
            src="/logo.png" 
            alt="mobbing-melden.de Logo" 
            className="h-12 w-auto"
          />
        </Link>

        <nav className="flex-1 space-y-2">
          <div className="bg-[#A0E8AF]/10 text-[#1A4731] rounded-xl px-4 py-3 font-medium flex items-center gap-3">
            <FolderOpen className="w-5 h-5" />
            Alle Fälle
          </div>
        </nav>

        <Button 
          variant="ghost" 
          onClick={handleLogout}
          className="text-[#718096] hover:text-[#2D3748] justify-start"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Abmelden
        </Button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="mobbing-melden.de Logo" 
              className="h-12 w-auto"
            />
          </Link>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-[#718096]"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-total">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#BDE0FE]/30 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[#1A365D]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>{stats.total}</p>
              <p className="text-sm text-[#718096]">Gesamt</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-open">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#A0E8AF]/30 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-[#1A4731]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>{stats.offen}</p>
              <p className="text-sm text-[#718096]">Offen</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-in-progress">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#F6E05E]/30 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#744210]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>{stats.in_bearbeitung}</p>
              <p className="text-sm text-[#718096]">In Bearbeitung</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-closed">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#718096]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>{stats.geschlossen}</p>
              <p className="text-sm text-[#718096]">Geschlossen</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h1 
            className="text-2xl md:text-3xl font-bold text-[#2D3748]"
            style={{ fontFamily: 'Nunito' }}
          >
            Alle Fälle
          </h1>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchData}
              className="rounded-xl"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suche nach Fall-ID oder Beschreibung..."
              className="input-base w-full pl-12 py-3"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#718096]" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-base py-3 pr-10"
              data-testid="status-filter"
            >
              <option value="alle">Alle Status</option>
              <option value="offen">Offen</option>
              <option value="in_bearbeitung">In Bearbeitung</option>
              <option value="geschlossen">Geschlossen</option>
            </select>
          </div>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#A0E8AF] mx-auto mb-4" />
              <p className="text-[#718096]">Lade Fälle...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-soft">
              <AlertCircle className="w-12 h-12 text-[#A0AEC0] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                Keine Fälle gefunden
              </h3>
              <p className="text-[#718096]">
                {searchTerm || filterStatus !== "alle" 
                  ? "Versuche eine andere Suche oder Filter"
                  : "Es wurden noch keine Meldungen eingereicht"}
              </p>
            </div>
          ) : (
            filteredReports.map((report) => {
              const status = STATUS_CONFIG[report.status] || STATUS_CONFIG.offen;
              const category = CATEGORY_CONFIG[report.category] || CATEGORY_CONFIG.andere;
              const StatusIcon = status.icon;

              return (
                <Link 
                  key={report.id} 
                  to={`/admin/fall/${report.id}`}
                  className="block"
                  data-testid={`report-card-${report.id}`}
                >
                  <div className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all border border-gray-50 hover:border-gray-100">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: status.bgColor, color: status.color }}
                          >
                            <StatusIcon className="w-3 h-3 inline mr-1" />
                            {status.label}
                          </span>
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${category.color}30`, color: category.color }}
                          >
                            {category.label}
                          </span>
                        </div>
                        <p className="text-[#2D3748] font-medium line-clamp-2 mb-2">
                          {report.description?.substring(0, 150)}
                          {report.description?.length > 150 && "..."}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-[#A0AEC0]">
                          <span>ID: {report.id?.substring(0, 8)}...</span>
                          <span>{formatDate(report.created_at)}</span>
                          {report.contact_info?.email && (
                            <span className="text-[#A0E8AF]">📧 Kontakt vorhanden</span>
                          )}
                          {report.file_ids?.length > 0 && (
                            <span className="text-[#BDE0FE]">📎 {report.file_ids.length} Datei(en)</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#A0AEC0] flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
